/**
 * V2.0.83 — Prompt suggestions carousel.
 *
 * One-click chips under the chat input, populated by the last 7 days
 * of chat outcome telemetry (from V2.0.82). Each chip fires `onSend`
 * with the label as the prompt. If the aggregate is thin, the
 * backend falls back to curated defaults.
 *
 * Kept intentionally minimal: no local state, no animations that
 * pull attention from the chat input. The chip row is hidden while
 * `disabled` (loading) so it doesn't invite a second click mid-turn.
 */
import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { getPromptSuggestions } from "../lib/api";

export default function PromptSuggestionsChips({ onSend, disabled }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPromptSuggestions(5, 168)
      .then((r) => {
        if (cancelled) return;
        setSuggestions(r.data?.suggestions || []);
        setLoaded(true);
      })
      .catch(() => {
        // Silent — this is a nice-to-have. No user-facing error.
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || suggestions.length === 0 || disabled) return null;

  return (
    <div
      className="border-t border-zinc-800 bg-zinc-950/60 px-3 py-2"
      data-testid="prompt-suggestions"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Sparkles className="w-3 h-3 text-brass-400" />
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
          // Try these — worked for others this week
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5" role="list">
        {suggestions.map((s, i) => (
          <button
            key={i}
            type="button"
            data-testid={`prompt-suggestion-${i}`}
            onClick={() => onSend?.(s.label, { source: "suggestion_chip" })}
            className={
              "px-2.5 py-1 text-[11px] rounded-sm border transition-colors font-mono " +
              (s.curated
                ? "border-zinc-700 bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                : "border-brass-700/40 bg-brass-950/30 text-brass-200 hover:bg-brass-900/40")
            }
            title={
              s.curated
                ? "Curated starter prompt"
                : `${s.count} architects committed a similar prompt in the last 7 days`
            }
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
